import {
  ACCESS_REQUEST_ERROR_CODES,
  ACCESS_REQUEST_FIELD_LIMITS,
  getAccessRequestSubmitErrorMessage,
  parseScopeListInput,
  validateScopeText,
  type AccessScopeType,
} from "@/lib/api/access-request-form";
import type {
  AccessRequest,
  AccessScope,
  ContestAccessRequestPayload,
  KeyEnvironment,
} from "@/lib/api/access-requests";
import { parseApiError } from "@/lib/api/api-error";
import {
  INTEGRATOR_REQUEST_FIELD_LIMITS,
  type IntegratorCatalogOptions,
} from "@/lib/api/integrator-request-form";
import type { LiveCatalogSelection } from "@/lib/api/live-catalog";
import type { ExpectedDailyVolume } from "@/lib/api/login-user";

export const CONTESTATION_MESSAGE_LIMITS = {
  min: 10,
  max: 1000,
} as const;

export interface ContestAccessRequestFormValues {
  /** Read-only for the UI; never sent in the contest payload. */
  environment: KeyEnvironment | null;
  integratorName: string;
  intendedUseCase: string;
  accessScopeType: AccessScopeType;
  namespacesText: string;
  locationNamesText: string;
  catalogSelections: LiveCatalogSelection[];
  /** Legacy contest only. */
  locationIdsText: string;
  expectedDailyVolume: ExpectedDailyVolume | "";
  contestationMessage: string;
}

function joinScopeList(values: string[] | undefined): string {
  return (values ?? []).join(", ");
}

export function createContestFormFromRequest(
  request: AccessRequest,
): ContestAccessRequestFormValues {
  const accessScopeType: AccessScopeType =
    request.access_scope === "locations" ? "locations" : "namespaces";

  return {
    environment: request.environment ?? null,
    integratorName: request.integrator_name?.trim() ?? "",
    intendedUseCase: request.intended_use_case ?? "",
    accessScopeType,
    namespacesText: joinScopeList(request.namespaces),
    locationNamesText: joinScopeList(request.location_names),
    catalogSelections: [],
    locationIdsText: joinScopeList(request.location_ids),
    expectedDailyVolume: request.expected_daily_volume ?? "",
    contestationMessage: "",
  };
}

export function isContestationMessageValid(value: string): boolean {
  const trimmed = value.trim();
  return (
    trimmed.length >= CONTESTATION_MESSAGE_LIMITS.min &&
    trimmed.length <= CONTESTATION_MESSAGE_LIMITS.max
  );
}

function isIntegratorContestForm(
  form: ContestAccessRequestFormValues,
): boolean {
  return form.environment === "sandbox" || form.environment === "live";
}

export function isContestAccessRequestFormValid(
  form: ContestAccessRequestFormValues,
  options: IntegratorCatalogOptions = {},
): boolean {
  if (!isContestationMessageValid(form.contestationMessage)) {
    return false;
  }

  const useCase = form.intendedUseCase.trim();
  if (
    useCase.length < ACCESS_REQUEST_FIELD_LIMITS.intendedUseCaseMin ||
    useCase.length > ACCESS_REQUEST_FIELD_LIMITS.intendedUseCaseMax
  ) {
    return false;
  }

  if (isIntegratorContestForm(form)) {
    const name = form.integratorName.trim();
    if (
      name.length < INTEGRATOR_REQUEST_FIELD_LIMITS.integratorNameMin ||
      name.length > INTEGRATOR_REQUEST_FIELD_LIMITS.integratorNameMax
    ) {
      return false;
    }

    if (form.environment === "sandbox") {
      return true;
    }

    if (options.useCatalog) {
      return form.catalogSelections.length > 0;
    }

    const scopeText =
      form.accessScopeType === "namespaces"
        ? form.namespacesText
        : form.locationNamesText;
    if (validateScopeText(scopeText)) {
      return false;
    }
    return parseScopeListInput(scopeText).length > 0;
  }

  // Legacy contest: scope + expected daily volume required.
  if (!form.expectedDailyVolume) {
    return false;
  }

  const scopeText =
    form.accessScopeType === "locations"
      ? form.locationIdsText
      : form.namespacesText;
  if (validateScopeText(scopeText)) {
    return false;
  }
  return parseScopeListInput(scopeText).length > 0;
}

/**
 * Builds the contest body. Never includes `environment` or `api_platform`.
 * Editable fields are always sent so the applicant can correct the prior denial.
 */
export function toContestAccessRequestPayload(
  form: ContestAccessRequestFormValues,
  options: IntegratorCatalogOptions = {},
): ContestAccessRequestPayload {
  const payload: ContestAccessRequestPayload = {
    contestation_message: form.contestationMessage.trim(),
    intended_use_case: form.intendedUseCase.trim(),
  };

  if (isIntegratorContestForm(form)) {
    payload.integrator_name = form.integratorName.trim();

    if (form.environment === "live") {
      // Always send both lists so contest can switch namespaces ↔ locations.
      // Omitted lists keep the prior value and the backend rejects mixed scope.
      if (options.useCatalog) {
        payload.access_scope = form.accessScopeType as AccessScope;
        if (form.accessScopeType === "namespaces") {
          payload.namespaces = form.catalogSelections.map(
            (item) => item.namespace,
          );
          payload.location_names = [];
          payload.location_ids = [];
        } else {
          payload.location_ids = form.catalogSelections.map((item) => item.id);
          payload.location_names = form.catalogSelections.map(
            (item) => item.name,
          );
          payload.namespaces = [];
        }
        return payload;
      }

      const values = parseScopeListInput(
        form.accessScopeType === "namespaces"
          ? form.namespacesText
          : form.locationNamesText,
      );
      payload.access_scope = form.accessScopeType as AccessScope;
      if (form.accessScopeType === "namespaces") {
        payload.namespaces = values;
        payload.location_names = [];
      } else {
        payload.location_names = values;
        payload.namespaces = [];
      }
    }

    return payload;
  }

  const values = parseScopeListInput(
    form.accessScopeType === "locations"
      ? form.locationIdsText
      : form.namespacesText,
  );
  payload.access_scope = form.accessScopeType as AccessScope;
  if (form.accessScopeType === "namespaces") {
    payload.namespaces = values;
    payload.location_ids = [];
  } else {
    payload.location_ids = values;
    payload.namespaces = [];
  }
  if (form.expectedDailyVolume) {
    payload.expected_daily_volume = form.expectedDailyVolume;
  }

  return payload;
}

const CONTEST_SUBMIT_MESSAGES = {
  invalidContestation:
    "Enter a contestation message between 10 and 1000 characters.",
  forbidden: "You do not have permission to contest this request.",
  notFound: "This access request could not be found.",
  illegalStatusTransition:
    "This request cannot be contested. It may already have been contested or is no longer declined.",
  integratorNameRequired: "Enter an integrator name of at least 3 characters.",
} as const;

export function getContestAccessRequestSubmitErrorMessage(
  err: unknown,
): string {
  const { status, code } = parseApiError(err);

  if (status === 409 && code === ACCESS_REQUEST_ERROR_CODES.illegalStatusTransition) {
    return CONTEST_SUBMIT_MESSAGES.illegalStatusTransition;
  }

  if (status === 403 && code === ACCESS_REQUEST_ERROR_CODES.forbidden) {
    return CONTEST_SUBMIT_MESSAGES.forbidden;
  }

  if (status === 404 && code === ACCESS_REQUEST_ERROR_CODES.notFound) {
    return CONTEST_SUBMIT_MESSAGES.notFound;
  }

  if (status === 400) {
    if (code === ACCESS_REQUEST_ERROR_CODES.invalidContestation) {
      return CONTEST_SUBMIT_MESSAGES.invalidContestation;
    }
    if (code === ACCESS_REQUEST_ERROR_CODES.integratorNameRequired) {
      return CONTEST_SUBMIT_MESSAGES.integratorNameRequired;
    }
  }

  return getAccessRequestSubmitErrorMessage(err);
}
